import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CategorySelect } from "@/components/CategorySelect";
import { Plus, PencilSimple, Trash, X, ArrowsLeftRight, CheckCircle, DownloadSimple, PaperPlaneTilt, CheckSquare, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { title: "", category: "", vendor_id: "", project_id: "", status: "draft", items: [{ description: "", quantity: 1, unit_price: 0 }], notes: "" };
const STATUS_VARIANT = { draft: "secondary", sent: "outline", accepted: "default", rejected: "destructive" };

export default function Quotations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [quotes, setQuotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // convert flow
  const [convertQuote, setConvertQuote] = useState(null);
  const [convProject, setConvProject] = useState("");
  const [convPhase, setConvPhase] = useState("");
  const [convPhases, setConvPhases] = useState([]);
  const [converting, setConverting] = useState(false);

  // share flow
  const [shareQuote, setShareQuote] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);

  // selection / delete flow
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = quotes.length > 0 && selected.length === quotes.length;
  const toggleAll = () => setSelected(allSelected ? [] : quotes.map((q) => q.id));
  const exitSelect = () => { setSelectMode(false); setSelected([]); };
  const askDeleteSingle = (q) => setPendingDelete([q]);
  const askDeleteSelected = () => { const list = quotes.filter((q) => selected.includes(q.id)); if (list.length) setPendingDelete(list); };
  const confirmDelete = async () => {
    if (!pendingDelete?.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/quotations/bulk-delete", { ids: pendingDelete.map((q) => q.id) });
      toast.success(`${data.deleted} quotation${data.deleted === 1 ? "" : "s"} moved to trash`);
      setPendingDelete(null); exitSelect(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const load = () => {
    api.get("/quotations").then((r) => setQuotes(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openConvert = (q) => {
    setConvertQuote(q);
    setConvProject("");
    setConvPhase("");
    setConvPhases([]);
  };

  const pickConvProject = async (pid) => {
    setConvProject(pid);
    setConvPhase("");
    const { data } = await api.get(`/projects/${pid}`);
    setConvPhases(data.phases || []);
  };

  const doConvert = async () => {
    if (!convProject || !convPhase) { toast.error("Select a project and phase"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/quotations/${convertQuote.id}/convert`, { project_id: convProject, phase_id: convPhase });
      toast.success(`${data.line_items_created} line items added to project`);
      setConvertQuote(null);
      load();
      navigate(`/projects/${convProject}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setConverting(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q.id);
    setForm({ title: q.title, category: q.category || "", vendor_id: q.vendor_id || "", project_id: q.project_id || "", status: q.status, items: q.items?.length ? q.items : [{ description: "", quantity: 1, unit_price: 0 }], notes: q.notes || "" });
    setOpen(true);
  };

  const setItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: val };
    setForm({ ...form, items });
  };
  const addRow = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeRow = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const formTotal = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      vendor_id: form.vendor_id === "none" ? "" : form.vendor_id,
      project_id: form.project_id === "none" ? "" : form.project_id,
      items: form.items.map((i) => ({ description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
    };
    try {
      if (editing) await api.put(`/quotations/${editing}`, payload);
      else await api.post("/quotations", payload);
      toast.success(editing ? "Quotation updated" : "Quotation created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(q.title || "quotation").replace(/ /g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) { toast.error("PDF download failed"); }
  };

  const doShare = async (e) => {
    e.preventDefault();
    setSharing(true);
    try {
      const { data } = await api.post(`/quotations/${shareQuote.id}/share`, { email: shareEmail });
      toast.success(data.message);
      setShareQuote(null);
      setShareEmail("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  return (
    <div>
      <PageHeader label="Estimates" title="Quotations">
        <Button onClick={openNew} className="rounded-none" data-testid="new-quote-button"><Plus size={16} className="mr-1" /> New Quotation</Button>
      </PageHeader>
      <div className="p-4 sm:p-8">
        {quotes.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="font-heading text-xl font-bold mb-2">No quotations yet</p>
            <p className="text-muted-foreground text-sm">Build estimates for future jobs and track their status.</p>
          </div>
        ) : (
          <>
          {isManager && (
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="quotes-toolbar">
              {selectMode ? (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-black" data-testid="select-all-quotes" />
                    Select all ({selected.length}/{quotes.length})
                  </label>
                  <div className="flex items-center gap-2">
                    <Button variant="destructive" disabled={!selected.length} onClick={askDeleteSelected} className="rounded-none" data-testid="delete-selected-quotes-button">
                      <Trash size={16} className="mr-1" /> Delete ({selected.length})
                    </Button>
                    <Button variant="outline" onClick={exitSelect} className="rounded-none" data-testid="cancel-select-quotes-button">Cancel</Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectMode(true)} data-testid="select-quotes-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <CheckSquare size={16} /> Select to delete
                  </button>
                  <button onClick={() => navigate("/trash?tab=quotations")} data-testid="view-trash-quotes-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <Trash size={16} /> Trash
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="border border-border reveal">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {selectMode && <TableHead className="w-10"></TableHead>}
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.id} data-testid={`quote-row-${q.id}`}
                    onClick={() => selectMode && toggleSelect(q.id)}
                    className={`hover:bg-secondary transition-colors duration-200 ${selectMode ? "cursor-pointer" : ""} ${selectMode && selected.includes(q.id) ? "bg-secondary" : ""}`}>
                    {selectMode && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.includes(q.id)} onChange={() => toggleSelect(q.id)} className="h-4 w-4 accent-black" data-testid={`select-quote-${q.id}`} />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{q.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{q.category || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{q.vendor_name || "—"}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[q.status]} className="rounded-none capitalize">{q.status}</Badge>{q.converted && <Badge variant="outline" className="rounded-none ml-1 text-emerald-700 border-emerald-600"><CheckCircle size={11} weight="fill" className="mr-1" />Converted</Badge>}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{q.items?.length || 0}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums font-semibold">{money(q.total)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!selectMode && (
                        <>
                          <button onClick={() => downloadPdf(q)} data-testid={`pdf-quote-${q.id}`} title="Download PDF" className="text-muted-foreground hover:text-primary mr-3"><DownloadSimple size={16} /></button>
                          <button onClick={() => { setShareQuote(q); setShareEmail(""); }} data-testid={`share-quote-${q.id}`} title="Email quotation" className="text-muted-foreground hover:text-primary mr-3"><PaperPlaneTilt size={16} /></button>
                          {q.status === "accepted" && !q.converted && (
                            <button onClick={() => openConvert(q)} data-testid={`convert-quote-${q.id}`} title="Convert to project line items" className="text-muted-foreground hover:text-emerald-700 mr-3"><ArrowsLeftRight size={16} /></button>
                          )}
                          <button onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`} className="text-muted-foreground hover:text-primary mr-3"><PencilSimple size={16} /></button>
                          {isManager && (
                            <button onClick={() => askDeleteSingle(q)} data-testid={`delete-quote-${q.id}`} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Quotation" : "New Quotation"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Title</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="quote-title-input" className="mt-2" placeholder="Foundation concrete estimate" />
              </div>
              <div>
                <Label className="label-caps">Category / Trade</Label>
                <CategorySelect categories={categories} value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testId="quote-category-select" placeholder="Select category" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="label-caps">Vendor</Label>
                <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="quote-vendor-select"><SelectValue placeholder="Vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Project</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="quote-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-caps">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addRow} className="rounded-none h-7" data-testid="add-quote-row"><Plus size={14} className="mr-1" /> Row</Button>
              </div>
              <div className="border border-border divide-y divide-border">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 p-2 items-center" data-testid={`quote-item-row-${idx}`}>
                    <Input className="col-span-6 rounded-none" placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`quote-item-desc-${idx}`} />
                    <Input className="col-span-2 rounded-none font-mono-fig" type="number" step="0.01" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} data-testid={`quote-item-qty-${idx}`} />
                    <Input className="col-span-3 rounded-none font-mono-fig" type="number" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`quote-item-price-${idx}`} />
                    <button type="button" onClick={() => removeRow(idx)} className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center"><X size={16} /></button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-3 gap-2 items-baseline">
                <span className="label-caps">Total</span>
                <span className="font-mono-fig text-xl font-semibold tabular-nums" data-testid="quote-total">{money(formTotal)}</span>
              </div>
            </div>

            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="quote-submit-button" className="rounded-none">{saving ? "Saving…" : editing ? "Update" : "Create Quotation"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={!!convertQuote} onOpenChange={(o) => !o && setConvertQuote(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">Convert to Project Line Items</DialogTitle></DialogHeader>
          {convertQuote && (
            <div className="space-y-4" data-testid="convert-dialog">
              <div className="border border-border p-4 bg-secondary">
                <p className="label-caps mb-1">Quotation</p>
                <div className="font-medium">{convertQuote.title}</div>
                <div className="text-sm text-muted-foreground">
                  {convertQuote.items?.length || 0} items · {convertQuote.category || "General"} · {money(convertQuote.total)}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Each quote item becomes a budgeted line item in the chosen project phase (budget auto-filled from the quote, actual starts at $0).
              </p>
              <div>
                <Label className="label-caps">Project</Label>
                <Select value={convProject} onValueChange={pickConvProject}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="convert-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Phase</Label>
                <Select value={convPhase} onValueChange={setConvPhase} disabled={!convProject}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="convert-phase-select"><SelectValue placeholder={convProject ? "Select phase" : "Pick a project first"} /></SelectTrigger>
                  <SelectContent>
                    {convPhases.map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button onClick={doConvert} disabled={converting || !convProject || !convPhase} data-testid="convert-submit-button" className="rounded-none">
                  <ArrowsLeftRight size={16} className="mr-1" />
                  {converting ? "Converting…" : "Convert"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!shareQuote} onOpenChange={(o) => !o && setShareQuote(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">Email Quotation</DialogTitle></DialogHeader>
          {shareQuote && (
            <form onSubmit={doShare} className="space-y-4" data-testid="share-dialog">
              <div className="border border-border p-4 bg-secondary">
                <div className="font-medium">{shareQuote.title}</div>
                <div className="text-sm text-muted-foreground">{money(shareQuote.total)} · PDF will be attached</div>
              </div>
              <div>
                <Label className="label-caps">Recipient email</Label>
                <Input type="email" required value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} data-testid="share-email-input" className="mt-2" placeholder="client@company.com" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={sharing} data-testid="share-submit-button" className="rounded-none">
                  <PaperPlaneTilt size={16} className="mr-1" />
                  {sharing ? "Sending…" : "Send Quotation"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete confirmation */}
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent className="rounded-none" data-testid="delete-quote-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} weight="fill" className="text-destructive" /> Delete {pendingDelete?.length === 1 ? "quotation" : `${pendingDelete?.length} quotations`}?</DialogTitle></DialogHeader>
          {pendingDelete && (
            <div className="space-y-4">
              <DialogDescription className="text-sm text-muted-foreground">{pendingDelete.length === 1 ? "This quotation" : "These quotations"} will be moved to <strong>Trash</strong>. Restore within 30 days before {pendingDelete.length === 1 ? "it's" : "they're"} permanently removed.</DialogDescription>
              <div className="border border-border max-h-48 overflow-y-auto">
                {pendingDelete.map((q) => (
                  <div key={q.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{q.title}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{money(q.total)}</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPendingDelete(null)} className="rounded-none">Cancel</Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={deleting} className="rounded-none" data-testid="delete-quote-confirm-button">
                  <Trash size={16} className="mr-1" />{deleting ? "Moving…" : "Move to Trash"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
